import { Check, X, DollarSign, Users, Trash2, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { PlayerProfileModal } from './PlayerProfileModal';

interface Participant {
  id: string;
  userId: string;
  status: string;
  paymentStatus: string;
  name?: string;
  email?: string;
  photoUrl?: string;
}

export const EnrollmentManagement = ({ competitionId, participants, categories, onUpdate }: { competitionId: string, participants: Participant[], categories?: string[], onUpdate: () => void }) => {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const token = localStorage.getItem('tt_auth_token');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(categories && categories.length > 0 ? categories[0] : '');
  const [filterCategory, setFilterCategory] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    setLoadingUsers(true);
    fetch(`${API_URL}/api/users`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => {
      if (Array.isArray(data)) {
        const validUsers = data;
        validUsers.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
        setUsers(validUsers);
      }
    })
    .catch(err => console.error(err))
    .finally(() => setLoadingUsers(false));
  }, []);

  const handleUpdate = async (id: string, updates: any) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/participants/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error('Falha ao atualizar');
      toast.success('Atualizado com sucesso');
      onUpdate();
    } catch(err) {
      console.error(err);
      toast.error('Erro ao atualizar participante');
    }
  };

  const handleAddParticipant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedEmails.length === 0) return;
    setIsAdding(true);
    try {
      const res = await fetch(`${API_URL}/api/competitions/${competitionId}/participants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ emails: selectedEmails, category: selectedCategory || null })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Falha ao adicionar participante');
      toast.success('Participante(s) adicionado(s) com sucesso');
      setSelectedEmails([]);
      setSearch('');
      setShowDropdown(false);
      onUpdate();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao adicionar participante');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteParticipant = async (userId: string) => {
    if (!window.confirm('Tem certeza que deseja remover este participante?')) return;
    try {
      const res = await fetch(`${API_URL}/api/competitions/${competitionId}/participants/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Falha ao remover');
      toast.success('Participante removido com sucesso');
      onUpdate();
    } catch(err) {
      console.error(err);
      toast.error('Erro ao remover participante');
    }
  };

  let filteredParticipants = participants;
  if (filterCategory !== 'ALL') {
    filteredParticipants = participants.filter(p => (p as any).category === filterCategory || (!filterCategory && !(p as any).category));
  }
  const pendingParticipants = filteredParticipants.filter(p => p.status === 'PENDING');
  const approvedParticipants = filteredParticipants.filter(p => p.status === 'APPROVED');
  const pendingPayment = approvedParticipants.filter(p => p.paymentStatus !== 'PAID');
  const paidParticipants = approvedParticipants.filter(p => p.paymentStatus === 'PAID');

  const renderList = (list: Participant[], showApproval: boolean, showPayment: boolean, showRemove: boolean) => {
    if (list.length === 0) {
      return <div style={{ padding: '16px', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Nenhum participante nesta lista.</div>;
    }
    return list.map(p => (
      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '12px 16px', borderRadius: '8px', marginBottom: '8px' }}>
        <div 
          style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
          onClick={() => setSelectedPlayerId(p.userId)}
        >
          {p.photoUrl ? (
            <img src={p.photoUrl} alt={p.name} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--primary-color)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
              {p.name ? (p.name.trim().split(/\s+/).length === 1 ? p.name.trim().split(/\s+/)[0].charAt(0).toUpperCase() : (p.name.trim().split(/\s+/)[0].charAt(0) + p.name.trim().split(/\s+/)[p.name.trim().split(/\s+/).length - 1].charAt(0)).toUpperCase()) : '?'}
            </div>
          )}
          <div>
            <div style={{ fontWeight: 600, color: 'white' }}>{p.name}</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', display: 'flex', gap: '8px' }}>
              <span>{p.email}</span>
              {(p as any).category && <span style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>{(p as any).category}</span>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {showApproval && (
            <>
              <button onClick={() => handleUpdate(p.id, { status: 'APPROVED' })} style={{ background: '#6FCF97', color: '#1a1a1a', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer' }} title="Aprovar">
                <Check size={18} />
              </button>
              <button onClick={() => handleUpdate(p.id, { status: 'REJECTED' })} style={{ background: '#EB5757', color: 'white', border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer' }} title="Rejeitar">
                <X size={18} />
              </button>
            </>
          )}
          {showPayment && (
            <button 
              onClick={() => handleUpdate(p.id, { paymentStatus: p.paymentStatus === 'PAID' ? 'PENDING' : 'PAID' })}
              style={{ background: p.paymentStatus === 'PAID' ? '#6FCF97' : 'rgba(255,255,255,0.1)', color: p.paymentStatus === 'PAID' ? '#1a1a1a' : 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
            >
              <DollarSign size={14} />
              {p.paymentStatus === 'PAID' ? 'Pago' : 'Marcar como Pago'}
            </button>
          )}
          {showRemove && (
            <button 
              onClick={() => handleDeleteParticipant(p.userId)}
              style={{ background: 'rgba(235, 87, 87, 0.1)', color: '#EB5757', border: '1px solid rgba(235, 87, 87, 0.3)', padding: '6px', borderRadius: '4px', cursor: 'pointer' }} title="Remover da Competição"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>
    ));
  };

  const filteredUsers = users.filter(u => 
    u.name?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(search.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()) || 
    u.email?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(search.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      <div style={{ background: 'var(--bg-panel)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 style={{ margin: '0 0 16px 0', color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <UserPlus size={18} /> Adicionar Participante
        </h3>
        <form onSubmit={handleAddParticipant} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input 
                type="text" 
                placeholder={loadingUsers ? "Carregando usuários..." : "Buscar jogador por nome ou e-mail..."}
                value={search}
                onChange={e => { setSearch(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'white', boxSizing: 'border-box' }}
              />
              {showDropdown && search && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#2a2a2a', zIndex: 10, maxHeight: '300px', overflowY: 'auto', borderRadius: '8px', marginTop: '4px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
                  {filteredUsers.map(u => (
                    <div 
                      key={u.id} 
                      onClick={() => {
                        setSelectedEmails(prev => prev.includes(u.email) ? prev.filter(e => e !== u.email) : [...prev, u.email]);
                      }}
                      style={{ padding: '10px 12px', cursor: 'pointer', color: 'white', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: selectedEmails.includes(u.email) ? 'rgba(255,255,255,0.15)' : 'transparent' }}
                      onMouseEnter={e => { if (!selectedEmails.includes(u.email)) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
                      onMouseLeave={e => { if (!selectedEmails.includes(u.email)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <input type="checkbox" checked={selectedEmails.includes(u.email)} readOnly style={{ cursor: 'pointer' }} />
                      <div>
                        <div>{u.name}</div>
                        <div style={{ opacity: 0.6, fontSize: '0.85em' }}>{u.email}</div>
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.5)' }}>Nenhum jogador encontrado</div>
                  )}
                </div>
              )}
            </div>
            
            {categories && categories.length > 0 && (
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                style={{ padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'white', outline: 'none' }}
              >
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}

            <button 
              type="submit" 
              disabled={isAdding || selectedEmails.length === 0}
              style={{ background: 'var(--primary-color)', color: '#1a1a1a', border: 'none', padding: '0 24px', borderRadius: '8px', fontWeight: 'bold', cursor: (isAdding || selectedEmails.length === 0) ? 'not-allowed' : 'pointer', opacity: (isAdding || selectedEmails.length === 0) ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {isAdding ? 'Adicionando...' : `Adicionar (${selectedEmails.length})`}
            </button>
          </div>

          {selectedEmails.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
              {selectedEmails.map(email => (
                <div key={email} style={{ background: 'rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {email}
                  <X size={14} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => setSelectedEmails(prev => prev.filter(e => e !== email))} />
                </div>
              ))}
            </div>
          )}

        </form>
      </div>

      {categories && categories.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <select 
            value={filterCategory} 
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: 'white', outline: 'none' }}
          >
            <option value="ALL">Todas as Categorias</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      <div style={{ background: 'var(--bg-panel)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 style={{ margin: '0 0 16px 0', color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Users size={18} /> Aguardando Aprovação ({pendingParticipants.length})
        </h3>
        {renderList(pendingParticipants, true, false, true)}
      </div>

      <div style={{ background: 'var(--bg-panel)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#FFAB00', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DollarSign size={18} /> Pendentes de Pagamento ({pendingPayment.length})
        </h3>
        {renderList(pendingPayment, false, true, true)}
      </div>

      <div style={{ background: 'var(--bg-panel)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#6FCF97', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Check size={18} /> Inscritos Confirmados ({paidParticipants.length})
        </h3>
        {renderList(paidParticipants, false, true, true)}
      </div>

      {!!selectedPlayerId && (
        <PlayerProfileModal
          onClose={() => setSelectedPlayerId(null)}
          playerId={selectedPlayerId}
        />
      )}
    </div>
  );
};
